/**
 * Dynamic block support (TD_DynBlocks).
 *
 * A dynamic block reference carries "custom properties" (flip states, visibility
 * states, positions, preset sizes…) that AutoCAD exposes in its Properties palette.
 * Changing one re-evaluates the block and swaps its anonymous representation (*U…).
 *
 * Requires DrawingWeb.wasm >= 27.7 — on 27.3 the classes are registered but reading
 * or writing a value throws (unbound types). Always gate on isDynBlockApiAvailable().
 *
 * ODA gotchas handled here (see types/DrawingWeb.extensions.d.ts):
 * - the OdDbDynBlockReference constructor takes an OdDbObjectId, not the entity;
 * - property values need two toString() hops to reach a JS string;
 * - setValue() must receive an OdDbEvalVariant (an allowed value, or one built
 *   with OdDbEvalVariant.init2/init3);
 * - graphics must be regenerated afterwards — recordGraphicsModified is not enough;
 * - the current value of a TEXT property (visibility state, lookup table) never
 *   makes it across — recovered from the block's representation cache instead,
 *   see blocks/dynamicRepData.ts.
 */
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode, DwgDataType } from "../core/constants";
import { isBlockReference } from "../entities/detection";
import { Logger } from "@/logger";
import type { DynBlockPropertyChange } from "../commands/SetDynBlockPropertyCommand";
import { alignCachedTextValues } from "./dynamicGrouping";
import type { DynBlockPropertyInfo } from "./dynamicGrouping";
import { readDynBlockTextValues } from "./dynamicRepData";

// Re-exported so callers have a single entry point for dynamic block concerns.
export {
  groupDynBlockProperties,
  getPanelRows,
  isVisibilityProperty,
  UNITS_ANGULAR,
  type DynBlockPropertyInfo,
  type DynBlockPointGroup,
  type DynBlockPropertyGroups,
  type DynBlockPanelRow,
  type DynBlockRowGlyph,
} from "./dynamicGrouping";

/**
 * Read a property value / allowed value as a JS string (double toString, see file header).
 *
 * Returns "" whenever the variant carries no readable payload — which is what ODA
 * hands back for the current value of every kDwgText property (see the file header).
 * patchTextValues() below recovers those; anything still empty means "unknown state",
 * never an error, and writing works regardless.
 */
function variantToString(variant: DrawingModule.OdDbEvalVariant): string {
  const asOdString = variant.toString();
  const result = String(asOdString);
  // toString() returns a fresh OdString wrapper; free it unless it is already a JS string.
  if (asOdString && typeof asOdString !== "string") deleteObj(asOdString);
  return result;
}

// The DwgDataType checks below stay inside functions: the constants module reads
// globalThis, populated only once the wasm is loaded — a module-scope dereference
// would crash any import chain evaluated without it (unit tests).

/** Properties whose current value ODA fails to return — see dynamicRepData.ts. */
const isTextType = (type: number): boolean => type === DwgDataType.kDwgText;

const isNumericType = (type: number): boolean =>
  [
    DwgDataType.kDwgReal,
    DwgDataType.kDwgInt8,
    DwgDataType.kDwgInt16,
    DwgDataType.kDwgInt32,
    DwgDataType.kDwgInt64,
  ].includes(type);

/**
 * True when the loaded DrawingWeb build can actually read/write dynamic properties.
 * 27.3 registers the classes but leaves OdDbEvalVariant's factories unbound.
 */
export function isDynBlockApiAvailable(module: DrawingModuleType): boolean {
  return (
    typeof module.OdDbDynBlockReference === "function" &&
    typeof module.OdDbEvalVariant?.init2 === "function"
  );
}

/**
 * True when a block *definition* is dynamic (carries an evaluation graph) — the
 * list-side counterpart of isDynamicBlockRef(), usable before any reference exists
 * (e.g. to badge the block lists of the drawer). Works on any database, including
 * the block template one.
 */
export function isDynamicBlockDefinition(
  module: DrawingModuleType,
  blockRecordId: DrawingModule.OdDbObjectId,
): boolean {
  if (typeof module.OdDbDynBlockTableRecord !== "function") return false;

  let dynRecord: DrawingModule.OdDbDynBlockTableRecord | null = null;
  try {
    dynRecord = new module.OdDbDynBlockTableRecord(blockRecordId);
    return dynRecord.isDynamicBlock();
  } catch {
    // Unbound class on older wasm builds, or a non-BTR id: simply "not dynamic".
    return false;
  } finally {
    deleteObj(dynRecord);
  }
}

/** True when the entity is a block reference backed by a dynamic block definition. */
export function isDynamicBlockRef(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): boolean {
  if (!isDynBlockApiAvailable(module)) return false;
  if (!isBlockReference(module, entityId)) return false;

  let dynRef: DrawingModule.OdDbDynBlockReference | null = null;
  try {
    dynRef = new module.OdDbDynBlockReference(entityId);
    return dynRef.isDynamicBlock();
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/dynamic.ts : isDynamicBlockRef",
    });
    return false;
  } finally {
    deleteObj(dynRef);
  }
}

/**
 * Name of the dynamic block definition behind a reference (e.g. "Fleche PR"),
 * as opposed to the anonymous *U… representation the reference actually points at.
 */
export function getDynamicBlockName(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): string | null {
  if (!isDynBlockApiAvailable(module)) return null;

  let dynRef: DrawingModule.OdDbDynBlockReference | null = null;
  let btrId: DrawingModule.OdDbObjectId | null = null;
  let btr: DrawingModule.OdDbBlockTableRecord | null = null;
  try {
    dynRef = new module.OdDbDynBlockReference(entityId);
    if (!dynRef.isDynamicBlock()) return null;
    btrId = dynRef.dynamicBlockTableRecord();
    if (!btrId || btrId.isNull()) return null;
    const pObj = btrId.safeOpenObject(OpenMode.kForRead, false);
    btr = module.OdDbBlockTableRecord.cast(pObj);
    if (!btr) {
      deleteObj(pObj);
      return null;
    }
    const odName = btr.getName();
    const name = odName.toString();
    deleteObj(odName);
    return name;
  } catch {
    return null;
  } finally {
    deleteObj(btr);
    deleteObj(btrId);
    deleteObj(dynRef);
  }
}

/**
 * Fill in the current value of text properties, which ODA returns empty.
 *
 * Reads the block's representation cache (see dynamicRepData.ts) and matches it to
 * the properties positionally — a match alignCachedTextValues() rejects unless it
 * checks out, leaving the values empty ("unknown state") rather than wrong.
 */
function patchTextValues(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  properties: DynBlockPropertyInfo[],
  types: number[],
): void {
  const indexes = types.flatMap((type, index) =>
    isTextType(type) ? [index] : [],
  );
  if (!indexes.length || indexes.every((index) => properties[index].value))
    return;

  const cached = readDynBlockTextValues(module, entityId);
  for (const [index, value] of alignCachedTextValues(
    properties,
    indexes,
    cached,
  )) {
    if (!properties[index].value) properties[index].value = value;
  }
}

/**
 * List the dynamic properties of a block reference.
 * Returns every property (including hidden/read-only ones, flagged accordingly) —
 * filtering for display is the UI layer's call.
 */
export function listDynBlockProperties(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): DynBlockPropertyInfo[] {
  if (!isDynBlockApiAvailable(module)) return [];

  let dynRef: DrawingModule.OdDbDynBlockReference | null = null;
  let props: DrawingModule.OdDbDynBlockReferencePropertyArray | null = null;
  const result: DynBlockPropertyInfo[] = [];
  // Raw ODA types, kept aside: only patchTextValues() needs them, not the UI.
  const types: number[] = [];

  try {
    dynRef = new module.OdDbDynBlockReference(entityId);
    if (!dynRef.isDynamicBlock()) return [];

    props = new module.OdDbDynBlockReferencePropertyArray();
    dynRef.getBlockProperties(props);

    for (let i = 0; i < props.size(); i++) {
      const prop = props.get(i);
      const odName = prop.propertyName();
      const name = odName.toString();
      deleteObj(odName);

      const currentValue = prop.value();
      const value = variantToString(currentValue);
      deleteObj(currentValue);

      const allowedValues: string[] = [];
      const allowed = new module.OdDbEvalVariantArray();
      try {
        prop.getAllowedValues(allowed);
        for (let j = 0; j < allowed.size(); j++) {
          const variant = allowed.get(j);
          allowedValues.push(variantToString(variant));
          deleteObj(variant);
        }
      } finally {
        deleteObj(allowed);
      }

      const type = prop.propertyType() as number;
      types.push(type);
      result.push({
        name,
        value,
        allowedValues,
        readOnly: prop.readOnly(),
        show: prop.show(),
        isNumeric: isNumericType(type),
        unitsType: prop.unitsType(),
      });
      deleteObj(prop);
    }

    patchTextValues(module, entityId, result, types);
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/dynamic.ts : listDynBlockProperties",
    });
  } finally {
    deleteObj(props);
    deleteObj(dynRef);
  }

  return result;
}

/**
 * Build the OdDbEvalVariant to hand to setValue().
 *
 * Constrained properties must receive one of their own allowed values (ODA compares
 * against the value set), so we look the target up there first and only fall back to
 * building a fresh variant for free numeric properties.
 * The caller owns the returned variant unless it came from `allowed` (freed with it).
 */
function buildTargetVariant(
  module: DrawingModuleType,
  allowed: DrawingModule.OdDbEvalVariantArray,
  newValue: string,
  isNumeric: boolean,
): { variant: DrawingModule.OdDbEvalVariant | null; ownedByCaller: boolean } {
  for (let j = 0; j < allowed.size(); j++) {
    const candidate = allowed.get(j);
    if (variantToString(candidate) === newValue) {
      return { variant: candidate, ownedByCaller: false };
    }
    deleteObj(candidate);
  }

  // No value set (or no match): free-form property.
  if (allowed.size() > 0) return { variant: null, ownedByCaller: false };

  if (isNumeric) {
    const numeric = Number(newValue);
    if (!Number.isFinite(numeric))
      return { variant: null, ownedByCaller: false };
    return {
      variant: module.OdDbEvalVariant.init2(numeric),
      ownedByCaller: true,
    };
  }

  const odValue = new module.OdString(newValue);
  const variant = module.OdDbEvalVariant.init10(odValue);
  deleteObj(odValue);
  return { variant, ownedByCaller: true };
}

/**
 * Apply a new value to one dynamic property.
 *
 * Mutates the database and returns the recorded change for the undo/redo command,
 * following the convention of photomarker/transform.ts. Returns null when nothing
 * changed or the property cannot be written — the caller then skips the command.
 *
 * Callers must regenerate graphics afterwards (viewer.forceRegen()).
 */
export function setDynBlockProperty(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  propertyName: string,
  newValue: string,
): DynBlockPropertyChange | null {
  if (!isDynBlockApiAvailable(module)) return null;

  // Old value read through listDynBlockProperties rather than prop.value(): text
  // properties only get a readable current value there (see patchTextValues), and
  // an empty one would leave undo with nothing to restore.
  const current = listDynBlockProperties(module, entityId).find(
    (prop) => prop.name === propertyName,
  );
  if (!current || current.readOnly) return null;
  const oldValue = current.value;
  if (oldValue === newValue) return null;

  let dynRef: DrawingModule.OdDbDynBlockReference | null = null;
  let props: DrawingModule.OdDbDynBlockReferencePropertyArray | null = null;
  let result: DynBlockPropertyChange | null = null;

  try {
    dynRef = new module.OdDbDynBlockReference(entityId);
    if (!dynRef.isDynamicBlock()) return null;

    props = new module.OdDbDynBlockReferencePropertyArray();
    dynRef.getBlockProperties(props);

    for (let i = 0; i < props.size(); i++) {
      const prop = props.get(i);
      const odName = prop.propertyName();
      const name = odName.toString();
      deleteObj(odName);

      if (name !== propertyName || prop.readOnly()) {
        deleteObj(prop);
        continue;
      }

      const allowed = new module.OdDbEvalVariantArray();
      prop.getAllowedValues(allowed);
      const { variant, ownedByCaller } = buildTargetVariant(
        module,
        allowed,
        newValue,
        current.isNumeric,
      );

      if (variant && prop.setValue(variant)) {
        result = { propertyName, oldValue, newValue };
      }

      if (ownedByCaller) deleteObj(variant);
      deleteObj(allowed);
      deleteObj(prop);
      break;
    }
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/dynamic.ts : setDynBlockProperty",
      propertyName,
    });
    return null;
  } finally {
    deleteObj(props);
    deleteObj(dynRef);
  }

  return result;
}

/**
 * Convert a point expressed in the block definition's coordinate system into world
 * coordinates, through the reference's blockTransform().
 */
function blockPointToWorld(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  point: { x: number; y: number },
): { x: number; y: number; z: number } | null {
  let blockRef: DrawingModule.OdDbBlockReference | null = null;
  let transform: DrawingModule.OdGeMatrix3d | null = null;
  let odPoint: DrawingModule.OdGePoint3d | null = null;
  try {
    const pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
    blockRef = module.OdDbBlockReference.cast(pObj);
    if (!blockRef) {
      deleteObj(pObj);
      return null;
    }
    transform = blockRef.blockTransform();
    odPoint = new module.OdGePoint3d(point.x, point.y, 0);
    odPoint.transformBy(transform);
    return { x: odPoint.x, y: odPoint.y, z: 0 };
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/dynamic.ts : blockPointToWorld",
    });
    return null;
  } finally {
    deleteObj(odPoint);
    deleteObj(transform);
    deleteObj(blockRef);
  }
}

/**
 * World position of an XY point property, i.e. where its canvas handle belongs.
 *
 * Property values are expressed in the block definition's coordinate system, so they
 * must go through the reference's blockTransform() to land on screen.
 */
export function getDynBlockPointWorldPosition(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
  propertyNameX: string,
  propertyNameY: string,
): { x: number; y: number; z: number } | null {
  const props = listDynBlockProperties(module, entityId);
  const x = Number(props.find((prop) => prop.name === propertyNameX)?.value);
  const y = Number(props.find((prop) => prop.name === propertyNameY)?.value);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return blockPointToWorld(module, entityId, { x, y });
}
